import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Badge,
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, MailReadRegular, MailUnreadRegular } from '@fluentui/react-icons';
import {
  IStatNotificationItem,
  NotificationStatus,
  NotificationStatusType,
} from '../../../notifications/StatNotification';

dayjs.extend(relativeTime);

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    listStyleType: 'none',
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    rowGap: tokens.spacingVerticalS,
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  itemUnread: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ...shorthands.borderLeft('3px', 'solid', tokens.colorCompoundBrandForeground1),
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
  },
  title: {
    flexGrow: 1,
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    display: 'flex',
    flexShrink: 0,
    columnGap: tokens.spacingHorizontalXXS,
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
  },
  description: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  },
  descriptionCollapsed: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  toggle: {
    alignSelf: 'flex-start',
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
  },
});

/** Descriptions longer than this get a "Read more" toggle. */
const TRUNCATE_LENGTH = 160;

interface INotificationListItemProps {
  item: IStatNotificationItem;
  onUpdate: (itemKey: string, status: NotificationStatusType) => void;
}

const NotificationListItem = ({ item, onUpdate }: INotificationListItemProps): JSX.Element => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isUnread = item.notificationStatus === NotificationStatus.Unread;
  const isTruncatable = item.description.length > TRUNCATE_LENGTH;

  return (
    <li className={mergeClasses(styles.item, isUnread && styles.itemUnread)}>
      <div className={styles.itemHeader}>
        <Text className={styles.title}>{item.title}</Text>
        {isUnread && (
          <Badge appearance="filled" color="brand" size="small">
            New
          </Badge>
        )}
        <div className={styles.actions}>
          <Button
            appearance="subtle"
            size="small"
            icon={isUnread ? <MailReadRegular /> : <MailUnreadRegular />}
            aria-label={
              isUnread ? `Mark "${item.title}" as read` : `Mark "${item.title}" as unread`
            }
            title={isUnread ? 'Mark as read' : 'Mark as unread'}
            onClick={() =>
              onUpdate(item.itemKey, isUnread ? NotificationStatus.Read : NotificationStatus.Unread)
            }
          />
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            aria-label={`Dismiss "${item.title}"`}
            title="Dismiss"
            onClick={() => onUpdate(item.itemKey, NotificationStatus.Deleted)}
          />
        </div>
      </div>
      <Text className={styles.timestamp} size={200}>
        {dayjs(item.timestamp).fromNow()}
      </Text>
      <Text
        className={mergeClasses(
          styles.description,
          isTruncatable && !isExpanded && styles.descriptionCollapsed,
        )}
        size={300}
      >
        {item.description}
      </Text>
      {isTruncatable && (
        <Button
          className={styles.toggle}
          appearance="transparent"
          size="small"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </li>
  );
};

interface IStatNotificationPanelProps {
  notifications: IStatNotificationItem[];
  onUpdateNotification: (itemKey: string, status: NotificationStatusType) => void;
}

/** Replaces `@coherence-design-system/notification-center`. */
const StatNotificationPanel = ({
  notifications,
  onUpdateNotification,
}: IStatNotificationPanelProps): JSX.Element => {
  const styles = useStyles();
  const visibleNotifications = notifications.filter(
    (notification) => notification.notificationStatus !== NotificationStatus.Deleted,
  );

  if (visibleNotifications.length === 0) {
    return (
      <Text className={styles.emptyState}>You&apos;re all caught up. No new notifications.</Text>
    );
  }

  return (
    <ul className={styles.list}>
      {visibleNotifications.map((notification) => (
        <NotificationListItem
          key={notification.itemKey}
          item={notification}
          onUpdate={onUpdateNotification}
        />
      ))}
    </ul>
  );
};

export default StatNotificationPanel;
